import { useCallback, useEffect, useMemo, useState } from "react";
import type { Draft } from "@reduxjs/toolkit";
import { isEmpty } from "lodash";
import cloneDeep from "lodash/cloneDeep";

import { selectVersionConfig, versionsSlice } from "fond/api";
import { Configuration } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import localStorage from "fond/utils/localStorage";

export interface ReturnType {
  /**
   * A collection of layerIds and the childIds to include.
   *
   * Example:
   *  {
   *    myGroupId: ["layer1", "layer2"]
   *  }
   */
  filters: Record<string, string[]>;
  /**
   * Callback to change the current filtering applied to the layerId
   *
   * @param layerId The id of the layer whose children are being filtered.
   * @param childId The id of the child being toggled on or off.
   * @param childIds A collection of all childIds available to be filtered.
   * @param multiple Flag indicating if multiple layers can be set to visible at the same time.
   * @param show Flag indicating if the childId should be shown or hidden.
   */
  onChange(layerId: string, childId: string, childIds: string[], multiple: boolean, show: boolean): void;
}

/**
 * Mark configurations as "Excluded" based on a filtered list of layer ids.
 */
export const markExcludedConfigs = (config: Draft<Configuration>, filteredLayerIds: Record<string, string[]>): Draft<Configuration> => {
  const configCopy = cloneDeep(config);

  const groupsIdsFiltered = new Set(Object.keys(filteredLayerIds));
  const inclusions = new Set(Object.values(filteredLayerIds).flat());

  configCopy.Data.ids.forEach((id) => {
    const entity = configCopy.Data.entities[id];

    if (entity?.Type === "LAYER" && entity.ParentID && groupsIdsFiltered.has(entity.ParentID)) {
      entity.Exclude = !inclusions.has(String(id));
    } else if (entity?.Type === "SUBLAYER" && entity.ParentID) {
      // Sublayers inherit the exclusion state of their parent.
      const parentLayer = configCopy.Data.entities[entity.ParentID] as LayerConfig;
      if (parentLayer.ParentID && groupsIdsFiltered.has(parentLayer.ParentID)) {
        entity.Exclude = !inclusions.has(String(parentLayer.ID));
      }
    }
  });

  return configCopy;
};

/**
 * Hook that updates a cached versionConfig by marking layers as "Excluded".
 * The filtered list is a whitelist of layers to be shown to the user.
 *
 * @param versionId the id of the versionConfig to be modified
 * @param defaultValues a set of default values to apply to the filters if no existing filters exist.
 * @param skipConfigUpdate if true the versionConfig will not be updated when filters change.
 *                         This is useful when you want to manage the config update yourself.
 */
export const useFilterLayers = (versionId?: string, defaultValues?: Record<string, string[]>, skipConfigUpdate?: boolean): ReturnType => {
  const dispatch = useAppDispatch();
  const currentData = useAppSelector((state) => selectVersionConfig(state, versionId));
  const [filteredLayerIds, setFilteredLayerIds] = useState<Record<string, string[]>>(
    localStorage.getItem(`state.project.projects[${versionId}].layerFilters`, defaultValues || {})
  );

  useEffect(() => {
    const newFilteredLayerIds = { ...defaultValues, ...filteredLayerIds };
    localStorage.setItem(`state.project.projects[${versionId}].layerFilters`, newFilteredLayerIds);

    // We don't apply any filtering if no layer ids is supplied
    if (!(skipConfigUpdate ?? false) && versionId && !isEmpty(newFilteredLayerIds)) {
      dispatch(
        versionsSlice.util.updateQueryData("getVersionConfig", versionId, (draft) =>
          Object.assign(draft, markExcludedConfigs(draft, newFilteredLayerIds))
        )
      );
    }
  }, [skipConfigUpdate, defaultValues, dispatch, filteredLayerIds, versionId, currentData]);

  /**
   * Callback function that handles the updating of the layers being filtered
   */
  const handleOnChange = useCallback((layerId: string, childId: string, optionIds: string[], multiple: boolean, show: boolean) => {
    if (multiple) {
      setFilteredLayerIds((prev) => {
        return {
          ...prev,
          [layerId]: prev[layerId]?.includes(childId) ? prev[layerId].filter((id) => id !== childId) : [...(prev[layerId] || []), childId],
        };
      });
    } else {
      setFilteredLayerIds((prev) => {
        return {
          ...prev,
          [layerId]: [childId],
        };
      });
    }
  }, []);

  const result = useMemo(() => {
    const newFilters = Object.keys(defaultValues || {}).reduce(
      (prev, curr) => {
        return {
          filters: {
            ...prev.filters,
            [curr]: filteredLayerIds[curr] || defaultValues?.[curr],
          },
        };
      },
      { filters: {} }
    ) as ReturnType;

    return { ...newFilters, onChange: handleOnChange };
  }, [defaultValues, filteredLayerIds, handleOnChange]);

  return result;
};
